import React, { useState } from "react";
import { auth, db } from "../../firebaseConfig";
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { doc, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate, Link, useLocation } from "react-router-dom";

const googleProvider = new GoogleAuthProvider();

const Login = () => {
  const [values, setValues] = useState({
    email: "",
    password: "",
    error: "",
    loading: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const { email, password, error, loading } = values;

  // Handle text-field changes
  const handleChange = (e) =>
    setValues({ ...values, [e.target.name]: e.target.value });

  // Handle classic email/password login form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setValues({ ...values, error: "All fields are required" });
      return;
    }

    setValues({ ...values, error: "", loading: true });

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);

      // Set isOnline=true in Firestore
      await updateDoc(doc(db, "users", result.user.uid), {
        isOnline: true,
      });

      setValues({ email: "", password: "", error: "", loading: false });

      // If we have a redirect "from", go there; otherwise go home
      if (location.state?.from) {
        navigate(location.state.from.pathname);
      } else {
        navigate("/", { replace: true });
      }
    } catch (error) {
      setValues({ ...values, error: error.message, loading: false });
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    setValues({ ...values, error: "", loading: true });

    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user doc exists; if not, create it
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Create new user document with basic info
        await setDoc(userRef, {
          uid: user.uid,
          name: user.displayName || "No Name",
          email: user.email,
          photoUrl: user.photoURL || "",
          photoPath: "",
          createdAt: new Date(),
          isOnline: true,
        });
      } else {
        // If user already exists, just update isOnline
        await updateDoc(userRef, {
          isOnline: true,
        });
      }

      setValues({ ...values, loading: false });

      // Redirect
      if (location.state?.from) {
        navigate(location.state.from.pathname);
      } else {
        navigate("/", { replace: true });
      }

    } catch (error) {
      setValues({ ...values, error: error.message, loading: false });
    }
  };

  return (
    <form className="shadow rounded p-3 mt-5 form" onSubmit={handleSubmit}>
      <h3 className="text-center mb-3">Log Into Your Account</h3>
      <div className="mb-3">
        <label htmlFor="email" className="form-label">
          Email
        </label>
        <input
          type="email"
          className="form-control"
          name="email"
          value={email}
          onChange={handleChange}
        />
      </div>
      <div className="mb-3">
        <label htmlFor="password" className="form-label">
          Password
        </label>
        <input
          type="password"
          className="form-control"
          name="password"
          value={password}
          onChange={handleChange}
        />
      </div>
      {error ? <p className="text-center text-danger">{error}</p> : null}

      {/* Classic login button */}
      <div className="text-center mb-3">
        <button className="btn btn-secondary btn-sm" disabled={loading}>
          Login
        </button>
      </div>

      {/* Google login button */}
      <div className="text-center mb-3">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleGoogleSignIn}
          disabled={loading}
        >
          Sign in with Google
        </button>
      </div>

      <div className="text-center mb-3">
        <small>
          <Link to="/auth/forgot-password">Forgot Password</Link>
        </small>
      </div>
    </form>
  );
};

export default Login;
